import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { setCartList } from "../sliceCart";
import { httpRequest } from "../../../Server/Axios";
import { Quicksale_SaveINV06QS } from "../../../Server/Apis";
import { fAlertToast } from "../../../Utility/Utilitys";
import {
  common_locaitonlist_API,
  INV06_MOBILE_GETROW,
} from "../../../Server/ApiV2";

const CartForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [locations, setLocations] = useState([]);

  const { UserID } = useSelector((state) => state.login.value);
  const { cartList } = useSelector((state) => state.sliceCart);

  const onSubmit = async (data) => {
    let cartDetails = cartList?.map((item) => ({
      ItmCd: item.ItmCd,
      Qty: item.qty,
      RSP: item.RSP,
      VATRSP: item.VATRSP,
      NETAmt: item.NetAmount,
      Barcode: item.BarCode,
    }));
    const payload = {
      v_ID: 0,
      v_Date: moment.utc(data?.deliveryDate).format("MM/DD/YYYY"),
      v_Loc: data?.location || "",
      v_User: UserID || "",
      v_MobileNo: data?.phoneNumber,
      v_CarNo: data?.carNumber,
      v_CarModel: data?.carModel,
      v_CarColor: data?.carColor,
      Details: cartDetails.length > 0 ? cartDetails : [],
    };
    const obj = Quicksale_SaveINV06QS(payload);
    let res = await httpRequest(obj);
    if (res?.ResultSet && res?.Type) {
      fAlertToast("SUCCESS", "Order placed successfully!");
    }
    dispatch(setCartList([]));
    navigate("/mposordertaking");
  };
  // Watch the phoneNumber field
  const phoneNumber = useWatch({ control, name: "phoneNumber" });
  //  function to call API
  const fetchCustomerData = useCallback(
    async (mobileNumber) => {
      if (/^[0-9]{10}$/.test(mobileNumber)) {
        try {
          const response = await INV06_MOBILE_GETROW({
            MobileNo: mobileNumber || "",
          });
          if (response?.status === 200) {
            const customerData = response?.data.ResultSet[0];
            if (customerData) {
              // Bind existing data to the form fields
              setValue("carNumber", customerData.CarNo || "");
              setValue("carModel", customerData.CarModel || "");
              setValue("carColor", customerData.CarColor || "");
            }
          }
        } catch (error) {
          console.error("Error fetching customer data:", error);
        }
      }
    },
    [setValue]
  );

  useEffect(() => {
    // Trigger the debounced API call when phoneNumber changes
    if (phoneNumber && phoneNumber.length === 10) {
      fetchCustomerData(phoneNumber);
    }
  }, [phoneNumber, fetchCustomerData]);

  const getLocation = useCallback(async () => {
    let locationList = await common_locaitonlist_API({
      usercd: UserID,
    });
    if (locationList?.status === 200) {
      if (locationList.data?.ResultSet?.length > 0) {
        setLocations(locationList?.data?.ResultSet);
        setValue("location", locationList?.data?.ResultSet[0]?.UId);
      }
    }
  }, [UserID]);

  useEffect(() => {
    setValue("deliveryDate", moment()); // Set default delivery date
    getLocation();
  }, [setValue, getLocation]);

  return (
    <Box
      sx={{
        height: { xs: "auto", md: "71vh" },
        margin: "auto",
        padding: 1,
        borderRadius: 2,
        backgroundColor: "#fff",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        overflow: "auto",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="phoneNumber"
          control={control}
          defaultValue=""
          rules={{
            required: "Phone number is required",
            pattern: {
              value: /^[0-9]{10}$/,
              message: "Phone number must be 10 digits",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Phone Number"
              variant="outlined"
              fullWidth
              size="small"
              margin="normal"
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
              InputProps={{
                sx: { fontSize: "14px" },
              }}
              InputLabelProps={{
                sx: { fontSize: "14px" },
              }}
            />
          )}
        />

        <Controller
          name="carModel"
          control={control}
          defaultValue=""
          rules={{ required: "Car model is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Car Model"
              variant="outlined"
              fullWidth
              size="small"
              margin="normal"
              error={!!errors.carModel}
              helperText={errors.carModel?.message}
              InputProps={{
                sx: { fontSize: "14px" },
              }}
              InputLabelProps={{
                sx: { fontSize: "14px" },
              }}
            />
          )}
        />

        <Controller
          name="carNumber"
          control={control}
          defaultValue=""
          rules={{
            required: "Car number is required",
            pattern: {
              value: /^[A-Z0-9-]+$/,
              message: "Car number must be alphanumeric (e.g., AB-1234)",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Car Number"
              variant="outlined"
              fullWidth
              size="small"
              margin="normal"
              error={!!errors.carNumber}
              helperText={errors.carNumber?.message}
              InputProps={{
                sx: { fontSize: "14px" },
              }}
              InputLabelProps={{
                sx: { fontSize: "14px" },
              }}
            />
          )}
        />

        <Controller
          name="carColor"
          control={control}
          defaultValue=""
          rules={{ required: "Car color is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Car Color"
              variant="outlined"
              fullWidth
              size="small"
              margin="normal"
              error={!!errors.carColor}
              helperText={errors.carColor?.message}
              InputProps={{
                sx: { fontSize: "14px" },
              }}
              InputLabelProps={{
                sx: { fontSize: "14px" },
              }}
            />
          )}
        />
        <Controller
          name="location"
          control={control}
          defaultValue=""
          rules={{
            required: "Location is required",
          }}
          render={({ field }) => (
            <FormControl
              fullWidth
              size="small"
              margin="normal"
              error={!!errors.location}
            >
              <InputLabel sx={{ fontSize: "14px" }}>Location</InputLabel>
              <Select {...field} label="Location" sx={{ fontSize: "14px" }}>
                {locations?.map((location) => (
                  <MenuItem value={location.UId} key={location.UId}>
                    {location.Name1}
                  </MenuItem>
                ))}
              </Select>
              {errors.location && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ marginLeft: "14px", fontSize: "12px" }}
                >
                  {errors.location.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
        <Controller
          name="deliveryDate"
          control={control}
          defaultValue={null}
          rules={{ required: "Delivery date is required" }}
          render={({ field }) => (
            <DatePicker
              {...field}
              label="Delivery Date"
              inputFormat="DD/MM/YYYY"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  margin="normal"
                  error={!!errors.deliveryDate}
                  helperText={errors.deliveryDate?.message}
                />
              )}
            />
          )}
        />

        <Stack direction={"row"} justifyContent={"center"}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="small"
            sx={{
              marginTop: 2,
              padding: 1,
              fontSize: "14px",
              width: "auto",
              backgroundColor: "#007BFF",
              ":hover": { backgroundColor: "#0056b3" },
            }}
            // onClick={handleSubmit(onSubmit)}
          >
            Place Order
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default CartForm;
